import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import { concepts, researchSources } from '../../data/researchDemo';
import type { ResearchSection } from '../../types/researchDemo';
import { focusRing, glassPanel } from '../../utils/demoUi';
const sequence = ['Source', 'Passage', 'Claim', 'Review', 'Knowledge'];
const shortcuts: {
  label: string;
  body: string;
  section: ResearchSection;
}[] = [{
  label: 'Read the sources.',
  body: 'Four fictional studies with exact passages and limits.',
  section: 'library'
}, {
  label: 'Compare findings.',
  body: 'Place studies side by side without flattening differences.',
  section: 'compare'
}, {
  label: 'Review relationships.',
  body: 'Follow approved connections back to their evidence.',
  section: 'map'
}, {
  label: 'Develop a question.',
  body: 'Write notes linked to the sources they discuss.',
  section: 'notebook'
}];
export function ResearchOverview() {
  const {
    state,
    dispatch
  } = useResearchDemo();
  const approved = state.claims.filter(claim => claim.status === 'approved').length;
  const awaiting = state.claims.filter(claim => claim.status === 'proposed').length;
  const stats = [{
    label: 'Source documents',
    value: researchSources.length
  }, {
    label: 'Controlled concepts',
    value: concepts.length
  }, {
    label: 'Approved relationships',
    value: approved
  }, {
    label: 'Awaiting review',
    value: awaiting
  }];
  return <div data-id="element-2226" data-mp="3u4hshfkoh1ts">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-ink-dark lg:text-4xl" data-id="element-2227" data-mp="3u49l237bqopz">
        Connect ideas. Inspect the evidence.
      </h1>
      <p className="mt-2 max-w-2xl text-lg leading-relaxed text-ink-muted" data-id="element-2228" data-mp="3kbas694soc0k">
        A research question spanning cognition, environmental design, sleep and public policy.
      </p>

      <section aria-labelledby="featured-question" className="mt-8 rounded-2xl bg-navy p-7 text-white lg:p-9" data-id="element-2229" data-mp="gnsaettgwgah">
        <p className="text-sm font-medium text-white/70" data-id="element-2230" data-mp="bvr0hejrv1nm">Featured question</p>
        <h2 id="featured-question" className="mt-2 font-heading text-2xl font-bold leading-snug lg:text-3xl" data-id="element-2231" data-mp="2644ezdzv2hqt">
          How might heat and cooling relate to attention at work?
        </h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-white/80" data-id="element-2232" data-mp="1wf48zamxym34">
          Trace a promising laboratory finding into the complexity of sleep, exposure and real-world working conditions.
        </p>
        <ol aria-label="How knowledge is built" className="mt-6 flex flex-wrap items-center gap-2 text-sm" data-id="element-2233" data-mp="1ndmyq8w7temc">
          {sequence.map((step, index) => <li key={step} className="flex items-center gap-2" data-id="element-2234" data-mp="1rh2vd58abez1" data-mpi={index}>
              <span className="rounded-md bg-white/10 px-3 py-1.5 font-semibold" data-id="element-2235" data-mp="1dh0ffojjevmf">{step}</span>
              {index < sequence.length - 1 && <ArrowRight size={14} aria-hidden="true" className="text-white/60" data-id="element-2236" data-mp="1ifldbhbbh37p" />}
            </li>)}
        </ol>
      </section>

      <dl className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-2237" data-mp="3ct2xqvgfdjyi">
        {stats.map(stat => <div key={stat.label} className={`${glassPanel} p-5`} data-id="element-2238" data-mp="115hjmp94kezu">
            <dt className="text-sm text-ink-muted" data-id="element-2239" data-mp="1160pv8pexey1">{stat.label}</dt>
            <dd className="mt-1 font-heading text-3xl font-bold text-ink-dark" data-id="element-2240" data-mp="w7pfmwv7h2fa">{stat.value}</dd>
          </div>)}
      </dl>

      <section aria-labelledby="workflow-shortcuts" className="mt-10" data-id="element-2241" data-mp="1awvgsqdlt2pt">
        <h2 id="workflow-shortcuts" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2242" data-mp="16429buqr3ga1">
          Where to start
        </h2>
        <ol className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-2243" data-mp="1jybmwlxdyio8">
          {shortcuts.map((shortcut, index) => <li key={shortcut.label} data-id="element-2244" data-mp="1tuenhncla992" data-mpi={index}>
              <button type="button" onClick={() => dispatch({
            type: 'setSection',
            section: shortcut.section
          })} className={`group flex h-full w-full items-start gap-4 rounded-xl border border-line bg-white/80 p-5 text-left hover:border-lavender/50 hover:bg-white transition-colors duration-150 ${focusRing}`} data-id="element-2245" data-mp="1ttderpj1pyui">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lavender-pale text-sm font-semibold text-lavender" data-id="element-2246" data-mp="2hq9niqvppxli">
                  {index + 1}
                </span>
                <span className="flex-1" data-id="element-2247" data-mp="2rneed40i6h8n">
                  <span className="block font-semibold text-ink-dark" data-id="element-2248" data-mp="2rofrrf4zrftj">{shortcut.label}</span>
                  <span className="mt-0.5 block text-sm text-ink-muted" data-id="element-2249" data-mp="2vozl07mg1zae">{shortcut.body}</span>
                </span>
                <ArrowRight size={18} aria-hidden="true" className="mt-1 text-navy" data-id="element-2250" data-mp="1yx5qqwn774mo" />
              </button>
            </li>)}
        </ol>
      </section>

      <p className="mt-10 text-sm leading-relaxed text-ink-muted" data-id="element-2251" data-mp="27yvsowephlyp">
        Not connected in this demonstration: live literature ingestion, AI extraction, external knowledge services and secure institutional permissions.
      </p>
    </div>;
}